import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ago } from '../../components/three/ecosystemState';
import { apiGet } from '../../utils/backendApi';
import { LiveSection } from './LiveFrame';

const SEV = ['critical', 'high', 'medium'];

function FindingsExpand({ slug }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  React.useEffect(() => {
    let live = true;
    apiGet(`/apps/admin/scans/${slug}`)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [slug]);
  if (err) return <p className="cs-help">Failed to load findings: {err}</p>;
  if (!data) return <p className="cs-help">Loading findings…</p>;
  const open = (data.findings || []).filter((f) => f.status === 'open' && !f.accepted);
  const accepted = (data.findings || []).filter((f) => f.accepted);
  return (
    <div>
      {!open.length && <p className="cs-help">No actionable open findings.</p>}
      {open.slice(0, 12).map((f) => (
        <p key={f.id} className="cs-help" style={{ margin: '4px 0' }}>
          <strong>{f.severity}</strong> · {f.name} · {f.scanner}
        </p>
      ))}
      {accepted.length ? <p className="cs-help">{accepted.length} accepted as the kit baseline.</p> : null}
    </div>
  );
}

// Org and operator rollup. Rows come from the fleet feed already on screen;
// the app page holds the ledger. Operators can expand a row into the
// existing per-slug findings endpoint.
export default function SecurityRollup({ rows, expandable = false }) {
  const [open, setOpen] = useState(null);
  const actionable = rows.filter((r) => (r.counts.critical || 0) + (r.counts.high || 0) > 0);
  const accepted = rows.reduce((n, r) => n + (r.counts.accepted || 0), 0);
  const scanning = rows.filter((r) => r.scanning).length;
  return (
    <>
      <p className="live-posture">
        {actionable.length} with high or critical findings
        {' · '}
        {accepted} accepted as the kit baseline
        {scanning ? ` · ${scanning} scanning` : ''}
      </p>
      <p className="live-accepted">
        High and critical are the ones to act on. Accepted counts are the shared kit baseline
        (inline scripts the bundle needs, missing subresource integrity) and are not an outage.
      </p>
      <LiveSection title="Apps" meta={`${rows.length} in this view`}>
        <div className="cs-plan-tablewrap">
          <table>
            <thead>
              <tr>
                <th>App</th><th>Assurance</th><th>Last scan</th>
                {SEV.map((k) => <th key={k}>{k}</th>)}
                <th>Accepted</th>
                {expandable && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.key}>
                  <tr>
                    <td>{r.href ? <Link to={r.href}>{r.name}</Link> : r.name} <span className="cs-help">{r.slug}</span></td>
                    <td>{r.assurance ? 'on' : 'off'}</td>
                    <td>{r.scanning ? 'scanning' : (r.lastScanAt ? ago(r.lastScanAt) : 'never')}</td>
                    {SEV.map((k) => <td key={k}>{r.counts[k] || 0}</td>)}
                    <td>{r.counts.accepted || 0}</td>
                    {expandable && (
                      <td>
                        <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => setOpen((v) => (v === r.slug ? null : r.slug))}>
                          {open === r.slug ? 'Hide' : 'Findings'}
                        </button>
                      </td>
                    )}
                  </tr>
                  {expandable && open === r.slug && (
                    <tr><td colSpan={8}><FindingsExpand slug={r.slug} /></td></tr>
                  )}
                </React.Fragment>
              ))}
              {!rows.length && <tr><td colSpan={expandable ? 8 : 7} className="cs-help">No apps in this view.</td></tr>}
            </tbody>
          </table>
        </div>
      </LiveSection>
    </>
  );
}
